// Waiver signing helpers (registration plan step 35, the screens over step
// 34's server; docs/pages/registration.md › "Waivers (step 34)" and
// "Waiver screens (step 35)"). Pure: no React and no memberApi value import,
// so memberApi, the registration, join and account controllers and admin-v2's
// Your team sheets can all use them.
//
// A signature is `{ name, version, key }`: the typed full name, and the
// version and opaque key of the exact text the read showed (the key, not the
// version alone, tells two waivers apart after an admin switches the attached
// one). The text itself is plain text: render it as a React text node, never
// as HTML or markdown (registration.md quirk 149).
//
// PDF waivers (owner, October 17; migration 220): a version is written text
// or an uploaded PDF. For a PDF the member opens it (a link, href checked by
// waiverPdfHref), ticks "I have read and agree" and types their full name;
// the signature then also carries `opened_at` and `agreed: true`. The opened
// state is in memory and per key: a new version (another key) locks again.

import { messages } from '@/i18n/runtime'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

/** The server's limit on a typed name (waivers.ts › SIGNER_MAX). */
export const WAIVER_NAME_MAX = 200

/** The registration labels in the active language (English until loaded). */
const words = () => messages(siteCoreMessages).member.registration

/**
 * The typed name's problem, or null. Mirrors backend
 * services/registration/waivers.ts › checkWaiverSignature's name rule: the
 * name is trimmed, must not be empty, must hold at least one letter and be
 * at most 200 characters. The server checks again; this only saves a
 * request that would come back as a 400.
 */
export function waiverNameProblem(raw: string): string | null {
  const name = (raw ?? '').trim()
  if (!name) return words().waiverNameRequired
  if (name.length > WAIVER_NAME_MAX) return words().waiverNameTooLong
  if (!/\p{L}/u.test(name)) return words().waiverNameRequired
  return null
}

/** The `waiver` body member: the trimmed name, and the version and key of
 *  the text that was read; for a PDF that was opened and agreed to (in this
 *  session, this key), also `opened_at` and `agreed: true`. Text bodies, and
 *  the two-argument calls (Your team, signing for a player), are unchanged. */
export function waiverSignBody(
  w: { version: number; key: string; pdf?: { url: string; bytes: number } },
  name: string,
  c?: WaiverConsent
): { name: string; version: number; key: string; opened_at?: string; agreed?: boolean } {
  const body: { name: string; version: number; key: string; opened_at?: string; agreed?: boolean } =
    { name: name.trim(), version: w.version, key: w.key }
  if (w.pdf && c && c.openedKey === w.key && c.openedAt && c.agreed) {
    body.opened_at = c.openedAt
    body.agreed = true
  }
  return body
}

/**
 * A PDF link the page may render as an href, or null: an http(s) URL with no
 * whitespace, quotes, angle brackets, backslashes or backticks, or the local
 * storage's '/api/v1/uploads/' path, at most 2048 characters. Anything else
 * (javascript:, data:, a protocol-relative //host) is null.
 */
export function waiverPdfHref(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '' || raw.length > 2048) return null
  if (/^https?:\/\/[^\s"'<>\\`]+$/.test(raw)) return raw
  if (raw.startsWith('/api/v1/uploads/') && !/[\s"'<>\\`]/.test(raw)) return raw
  return null
}

/** A PDF's size for people: "240 KB" / "1.2 MB". */
export function waiverPdfSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  const mb = bytes / (1024 * 1024)
  return `${mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10} MB`
}

/** The member's consent to a PDF waiver, in memory: which version (key) they
 *  opened and when, and whether they ticked "I have read and agree". */
export interface WaiverConsent {
  openedKey: string | null
  openedAt: string | null
  agreed: boolean
}

export const WAIVER_CONSENT_NONE: WaiverConsent = { openedKey: null, openedAt: null, agreed: false }

/** Record that the PDF of version `key` was opened now (the tick is kept only
 *  for the same key). */
export function openWaiver(c: WaiverConsent, key: string): WaiverConsent {
  return { openedKey: key, openedAt: new Date().toISOString(), agreed: c.openedKey === key ? c.agreed : false }
}

/** Whether this PDF waiver (this key) was opened in this session. */
export function waiverOpened(w: { key: string; pdf?: unknown }, c: WaiverConsent): boolean {
  return !!w.pdf && c.openedKey === w.key
}

/** What stops a PDF waiver's signature (mirrors the server's messages), or
 *  null; always null for a text waiver. */
export function waiverConsentProblem(
  w: { key: string; pdf?: unknown },
  c: WaiverConsent
): { target: 'open' | 'agree'; message: string } | null {
  if (!w.pdf) return null
  if (!waiverOpened(w, c)) return { target: 'open', message: words().waiverConsentOpen }
  if (!c.agreed) return { target: 'agree', message: words().waiverConsentAgree }
  return null
}

/**
 * Where a 400's `fields.waiver` belongs (registration.md quirk 150): a custom
 * question keyed `waiver` shares the key, and its error wins on the server,
 * so the message is the question's then; otherwise it is the waiver step's.
 */
export function waiverFieldTarget(questionKeys: readonly string[]): 'question' | 'waiver' {
  return questionKeys.includes('waiver') ? 'question' : 'waiver'
}

export interface WaiverText {
  title: string
  version: number
  key: string
  body: string
  /** A PDF version: its URL (checked by waiverPdfHref) and size. */
  pdf?: { url: string; bytes: number }
}

/**
 * A waiver's text from the wire (a read's `registration.waiver`, or a 409
 * waiver_changed's `waiver`), as a whitelist: a string title and body, a
 * positive integer version, a key of 1 to 64 characters and, for a PDF
 * version, `pdf: { url, bytes }` (a safe href and a positive integer size;
 * an unsafe one makes the whole read null). Anything else, or a missing
 * member, is null; extra members are dropped.
 */
export function parseWaiverText(raw: unknown): WaiverText | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const { title, version, key, body } = r
  if (typeof title !== 'string' || typeof body !== 'string') return null
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) return null
  if (typeof key !== 'string' || key === '' || key.length > 64) return null
  if (r.pdf !== undefined && r.pdf !== null) {
    const p = r.pdf as Record<string, unknown>
    if (typeof p !== 'object' || Array.isArray(p)) return null
    const url = waiverPdfHref(p.url)
    const bytes = p.bytes
    if (!url || typeof bytes !== 'number' || !Number.isInteger(bytes) || bytes < 1) return null
    return { title, version, key, body, pdf: { url, bytes } }
  }
  return { title, version, key, body }
}
